import {
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { messageTypes } from '@clatter/ui';
import {
  hasRole,
  getAxiosCmsWithAuthorization,
  strapiCoBrandLogoToStoreMap,
  ENABLE_CO_BRAND_LOGO_REL,
  requestStatus,
} from '@clatter/platform';
import { createMessage, messagesActions } from './messages.slice';
import { pagesAdapter } from './pages.slice';
import { userRolesMap } from '../constants';
import micrositesApi from "../api/microsites.api";

export const MICROSITES_FEATURE_KEY = 'microsites';

export const micrositesAdapter = createEntityAdapter({
  selectId: (row) => row.id,
  sortComparer: (a, b) => (a.updated_at > b.updated_at ? -1 : 1),
});
const apiBaseUrl = '/microsites';

const getUserByEmail = async (email) => {
  const response = await getAxiosCmsWithAuthorization({
    method: 'get',
    url: `/users?filters[email][$eq]=${email}`,
  });
  const user = response.data[0];
  return user;
};

const getNewSiteName = () => {
  return 'Default Microsite ' + Math.random().toString(36).slice(-4);
};

export const setIsCreatingMicrosite = createAction(
  'SET_IS_CREATING_MICROSITE',
);

export const createMicrosite = createAsyncThunk(
  `${MICROSITES_FEATURE_KEY}/create`,
  async (email) => {
    const response = await getAxiosCmsWithAuthorization({
      method: 'post',
      url: apiBaseUrl,
      data: {
        data: {
          name: getNewSiteName(),
          published: false,
          pages: [],
          owner: email,
          c_created_by: email,
        },
      },
    });

    return Promise.resolve(mapApiToStore(response.data.data));
  },
);

export const updateMicrosite = createAsyncThunk(
  `${MICROSITES_FEATURE_KEY}/update`,
  async (formData, { dispatch, rejectWithValue }) => {
    const payload = {
      ...formData,
    }

    if (ENABLE_CO_BRAND_LOGO_REL && payload?.cobrand_logo_rel?.id) {
      // don't send legacy logo data
      if (payload?.cobrand_logo) {
        delete payload.cobrand_logo;
      }

      // set relation properly
      payload['cobrand_logo_rel'] = { set: [formData?.cobrand_logo_rel.id] };
    }

    try {
      const response = await getAxiosCmsWithAuthorization({
        method: 'put',
        url: `${apiBaseUrl}/${formData.documentId}?populate[0]=pages.page_template&populate[1]=cobrand_logo_rel.asset&populate[2]=cobrand_logo_rel.owner`,
        data: {
          data: mapStoreToApiUpdateMicrosite(payload),
        },
      });

      return mapApiToStore(response.data.data);
    } catch (error) {
      dispatch(
        messagesActions.setMessage({
          message: createMessage(
            messageTypes.error,
            'There was an error saving the microsite. Try changing the name and Save again.',
            'updateMicrosite',
          ),
        }),
      );
      return rejectWithValue(error.response.data);
    }
  },
);

// @todo: (almost) 1:1 copy of fetchMicrosites from below; refactor to fetchMicrosites
export const fetchAllMicrosites = createAsyncThunk(
  `${MICROSITES_FEATURE_KEY}/fetchall`,
  async ({ user = null }) => {
    // let's restrict the query by default
    // NOTE: if user will be "undefined" or "null" strapi will
    // correctly return an empty array, not breaking the UI...
    let restrict = `&filters[owner][$eq]=${encodeURIComponent(user.email)}`;

    // if conditions are met remove restriction ang fetch all data
    if (user && hasRole(userRolesMap.admin, user)) {
      restrict = '';
    }

    const response = await getAxiosCmsWithAuthorization({
      method: 'get',
      url: `${apiBaseUrl}?pagination[limit]=false&populate[pages][count]=true${restrict}`,
    });

    return Promise.resolve(response.data);
  },
);

export const fetchMicrosites = createAsyncThunk(
  `${MICROSITES_FEATURE_KEY}/fetch`,
  async ({ user = null, fetchAll = false, micrositeId = null }) => {
    // let's restrict the query by default
    // NOTE: if user will be "undefined" or "null" strapi will
    // correctly return an empty array, not breaking the UI...
    let restrict = `&filters[owner][$eq]=${encodeURIComponent(user.email)}`;

    // if conditions are met remove restriction ang fetch all data
    if (user && hasRole(userRolesMap.admin, user) || fetchAll) {
      restrict = '';
    }

    if (micrositeId) {
      restrict += `&filters[id][$eq]=${micrositeId}`;
    }

    const response = await getAxiosCmsWithAuthorization({
      method: 'get',
      url: `${apiBaseUrl}?pagination[limit]=false&populate[0]=cobrand_logo&populate[1]=pages.page_template&populate[2]=cobrand_logo_rel.asset&populate[3]=cobrand_logo_rel.owner${restrict}`,
    });

    return Promise.resolve(response.data);
  },
);

// @todo: this is unused - remove
export const fetchMicrositeById = createAsyncThunk(
  `${MICROSITES_FEATURE_KEY}/fetchSingle`,
  async (micrositeId) => {
    const response = await getAxiosCmsWithAuthorization({
      method: 'get',
      url: `${apiBaseUrl}/${micrositeId}`,
    });

    return Promise.resolve(response.data);
  },
);

export const deleteMicrosite = createAsyncThunk(
  `${MICROSITES_FEATURE_KEY}/delete`,
  async ({ micrositeId, documentId }, { rejectWithValue, getState }) => {
    try {
      if (!micrositeId) {
        throw new Error('Error deleting microsite. No microsite ID provided! Please contact the administrator.');
      }

      if (isNaN(micrositeId)) {
        throw new Error('Error deleting microsite. Invalid microsite ID provided! Please contact the administrator.');
      }

      await micrositesApi.deleteMicrosite({ documentId: documentId });

      // get removed microsite data so we could
      // update store and create success message
      const microsite = getState()[MICROSITES_FEATURE_KEY].entities[micrositeId];
      const micrositeToDeletePagesIds = microsite?.pages?.map(page => page.id);

      return {
        micrositeId: micrositeId,
        pageIds: micrositeToDeletePagesIds,
        message: `Microsite "${microsite.name}" has been successfully deleted.`
      };
    } catch (error) {

      return rejectWithValue({
        response: error?.response || null,
        message: error?.response?.data?.message || error?.message || 'Error deleting microsite!',
        status: error?.response?.status,
      });
    }
  },
);

export const deleteMicrosites = createAsyncThunk(
  `${MICROSITES_FEATURE_KEY}/deleteBulk`,
  async (microsites) => {
    const deletedPages = await Promise.all(
      microsites
        .reduce(
          (acc, microsite) => [...acc, ...microsite.pages.map(({ id }) => id)],
          [],
        )
        .map((pageId) => getAxiosCmsWithAuthorization({
          method: 'delete',
          url: `/pages/${pageId}`,
        })),
    );

    const deletedMicrosites = await Promise.all(
      microsites.map((microsite) =>
        getAxiosCmsWithAuthorization({
          method: 'delete',
          url: `${apiBaseUrl}/${microsite.id}`,
        }),
      ),
    );

    return Promise.resolve({
      pageIds: deletedPages.map((response) => response.data.id),
      micrositeIds: deletedMicrosites.map((response) => response.data.id),
    });
  },
);

export const cloneMicrosite = createAsyncThunk(`${MICROSITES_FEATURE_KEY}/clone`, async ({ micrositeId, documentId }, { rejectWithValue }) => {
  try {
    if (!micrositeId) {
      throw new Error('Error cloning microsite. No microsite ID provided! Please contact the administrator.');
    }

    if (isNaN(micrositeId)) {
      throw new Error('Error cloning microsite. Invalid microsite ID provided! Please contact the administrator.');
    }

    const response = await micrositesApi.postMicrosite({ data: { cloneMicrositeId: documentId } });

    return mapApiToStore(response.data.data);

  } catch (error) {
    return rejectWithValue({
      response: error?.response || null,
      message: error?.response?.data?.message || error?.message || 'Error cloning microsite!',
      status: error?.response?.status,
    });
  }
});

export const initialMicrositesState = micrositesAdapter.getInitialState({
  loadingStatus: requestStatus.initial,
  isCreatingMicrosite: false,
  error: null,
});

const mapApiToStore = (item) => ({
  id: item.id,
  documentId: item.documentId,
  ...item.attributes,
  ...(ENABLE_CO_BRAND_LOGO_REL ?
    { cobrand_logo_rel: strapiCoBrandLogoToStoreMap(item?.attributes?.cobrand_logo_rel?.data) } :
    {}
  ),
  cobrand_logo: {
    id: item?.attributes?.cobrand_logo?.data?.id,
    documentId: item?.attributes?.cobrand_logo?.data?.documentId,
    ...(item?.attributes?.cobrand_logo?.data?.attributes || {})
  },
  pages: item.attributes?.pages?.data && Array.isArray(item.attributes?.pages?.data) ? item.attributes?.pages?.data.map(page => ({
    id: page.id,
    documentId: page.documentId,
    name: page.attributes?.title,
    ...page.attributes,
    page_template: page.attributes?.page_template && page.attributes?.page_template?.data !== null ? {
      id: page.attributes?.page_template?.data?.id,
      documentId: page.attributes?.page_template?.data?.documentId,
      ...page.attributes?.page_template?.data?.attributes,
    } : null,
  })) : [],
  pagesCount: (() => {
    if (item.attributes?.pages?.data && Array.isArray(item.attributes?.pages?.data)) {
      return item.attributes?.pages?.data.length;
    }

    return item.attributes?.pages?.data?.attributes?.count || 0;
  })(),
});

const mapStoreToApiUpdateMicrosite = (item) => ({
  name: item.name,
  published: item.published,
  client_name: item.client_name,
  password_hash: item.password_hash,
  c_created_by: item.c_created_by,
  c_updated_by: item.c_updated_by,
  owner: item.owner,
  publishedAt: item.publishedAt,
  pages: item.pages,
  cobrand_logo_rel: item.cobrand_logo_rel,
});

export const micrositesSlice = createSlice({
  name: MICROSITES_FEATURE_KEY,
  initialState: initialMicrositesState,
  reducers: {
    add: micrositesAdapter.addOne,
    remove: micrositesAdapter.removeOne,
  },
  extraReducers: (builder) => {
    builder
      .addCase(setIsCreatingMicrosite, (state, { payload }) => {
        state.isCreatingMicrosite = payload;
      })
      .addCase(fetchMicrosites.pending, (state) => {
        state.loadingStatus = 'loading';
      })
      .addCase(fetchMicrosites.fulfilled, (state, action) => {
        micrositesAdapter.setAll(state, (action.payload.data || []).map(mapApiToStore));
        state.loadingStatus = 'loaded';
      })
      .addCase(fetchMicrosites.rejected, (state, action) => {
        state.loadingStatus = 'error';
        state.error = action.error.message;
      })
      .addCase(fetchAllMicrosites.pending, (state) => {
        state.loadingStatus = 'loading';
      })
      .addCase(fetchAllMicrosites.fulfilled, (state, action) => {
        micrositesAdapter.setAll(state, (action.payload.data || []).map(mapApiToStore));
        state.loadingStatus = 'loaded';
      })
      .addCase(fetchAllMicrosites.rejected, (state, action) => {
        state.loadingStatus = 'error';
        state.error = action.error.message;
      })
      .addCase(updateMicrosite.pending, (state) => {
        state.loadingStatus = 'loading';
      })
      .addCase(updateMicrosite.fulfilled, (state, action) => {
        const { id, ...changes } = action.payload;
        micrositesAdapter.updateOne(state, {
          id: id,
          changes: changes,
        });
        state.loadingStatus = requestStatus.fulfilled;
      })
      .addCase(updateMicrosite.rejected, (state, action) => {
        state.loadingStatus = 'error';
        state.error = action.error.message;
      })
      .addCase(deleteMicrosite.pending, (state) => {
        state.loadingStatus = requestStatus.pending;
      })
      .addCase(deleteMicrosite.fulfilled, (state, action) => {
        micrositesAdapter.removeOne(state, action.payload.micrositeId);
        pagesAdapter.removeMany(state, action.payload.pageIds);
        state.loadingStatus =  requestStatus.fulfilled;
      })
      .addCase(deleteMicrosite.rejected, (state) => {
        state.loadingStatus = requestStatus.error;
      })
      .addCase(deleteMicrosites.pending, (state) => {
        state.loadingStatus = 'loading';
      })
      .addCase(deleteMicrosites.fulfilled, (state, action) => {
        micrositesAdapter.removeMany(state, action.payload.micrositeIds);
        pagesAdapter.removeMany(state, action.payload.pageIds);
        state.loadingStatus = 'loaded';
      })
      .addCase(cloneMicrosite.pending, (state) => {
        state.loadingStatus = requestStatus.pending;
      })
      .addCase(cloneMicrosite.fulfilled, (state, action) => {
        micrositesAdapter.addOne(state, action.payload);
        state.loadingStatus = requestStatus.fulfilled;
      })
      .addCase(cloneMicrosite.rejected, (state) => {
        state.loadingStatus = requestStatus.error;
      })
      .addCase(fetchMicrositeById.pending, (state) => {
        state.loadingStatus = 'loading';
      })
      .addCase(fetchMicrositeById.fulfilled, (state, action) => {
        micrositesAdapter.addOne(state, action.payload);
        state.loadingStatus = 'loaded';
      });
  },
});

export const micrositesReducer = micrositesSlice.reducer;
export const micrositesActions = micrositesSlice.actions;

const { selectAll, selectEntities } = micrositesAdapter.getSelectors();

export const getMicrositesState = (rootState) =>
  rootState[MICROSITES_FEATURE_KEY];

export const selectAllMicrosites = createSelector(
  getMicrositesState,
  selectAll,
);

export const selectMicrositesEntities = createSelector(
  getMicrositesState,
  selectEntities,
);

export const selectMicrositesLoadingStatus = createSelector(
  getMicrositesState,
  (state) => state.loadingStatus,
);
